import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { FasatMessageCard } from './app/common/components';
import { staticLabelKeys } from './app/modules/activity/moduleConstants';
import { globalConstants, staticCommonLabelKeys } from './app/common/moduleConstants';

const { serverErrorMsgContainer } = globalConstants;

// Configuration for allowed HTML tags and attributes
const HTML_SANITIZATION_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'span', 'a', 'ul', 'ol', 'li', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

// Default values for message configuration
const DEFAULT_CONFIG = {
  title: staticCommonLabelKeys.COMMON_LABEL_IMPORTANT_INFO,
  variant: 'primary_information',
  isActive: true,
  showCloseButton: true,
};

// Supported message variants
const SUPPORTED_VARIANTS = [
  'primary_information',
  'primary_alert',
  'primary_warning',
  'primary_success',
];

/**
 * Safely parses JSON string with error handling
 * Supports both simple JSON, multi-locale structure, and arrays of messages
 * @param {string} jsonString - JSON string to parse
 * @param {string} currentLocale - Current application locale (e.g., 'en', 'fr')
 * @returns {Array|null} Array of parsed objects or null if parsing fails
 */
const parseImportantInfoJson = (jsonString, currentLocale = 'en') => {
  if (!jsonString || typeof jsonString !== 'string') {
    return null;
  }

  try {
    const parsedData = JSON.parse(jsonString);

    // Validate required fields exist
    if (!parsedData || typeof parsedData !== 'object') {
      return null;
    }

    // If it's already an array, return it
    if (Array.isArray(parsedData)) {
      return parsedData;
    }

    // Check if it's a multi-locale structure (e.g., { en: {...}, fr: {...} })
    if (parsedData[currentLocale] || parsedData.en) {
      const localeData = parsedData[currentLocale] || parsedData.en;
      // If locale data is an array, return it; otherwise wrap in array
      return Array.isArray(localeData) ? localeData : [localeData];
    }

    // Single locale structure - wrap in array for consistency
    return [parsedData];
  } catch (error) {
    // Silently fail and return null for invalid JSON
    return null;
  }
};

/**
 * Sanitizes HTML content using DOMPurify to prevent XSS attacks
 * @param {string} htmlString - HTML string to sanitize
 * @returns {string} Sanitized HTML string
 */
const sanitizeHtmlContent = (htmlString) => {
  if (!htmlString || typeof htmlString !== 'string') {
    return '';
  }

  // Sanitize the HTML with configured rules
  const sanitizedHtml = DOMPurify.sanitize(htmlString, HTML_SANITIZATION_CONFIG);

  // Add rel="noopener noreferrer" to all external links for security
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sanitizedHtml;

  const links = tempDiv.querySelectorAll('a[target="_blank"]');
  links.forEach((link) => {
    link.setAttribute('rel', 'noopener noreferrer');
  });

  return tempDiv.innerHTML;
};

/**
 * Determines if a message should be displayed based on isActive/active flags
 * @param {boolean|undefined} isActive - Primary active flag
 * @param {boolean|undefined} active - Fallback active flag
 * @returns {boolean} Whether the message is active
 */
const isMessageActiveStatus = (isActive, active) => {
  if (isActive !== undefined) {
    return isActive;
  }
  if (active !== undefined) {
    return active;
  }
  return true;
};

/**
 * Validates message content and returns sanitized content or null
 * @param {string|undefined} message - Primary message content
 * @param {string|undefined} content - Fallback message content
 * @returns {string|null} Sanitized message content or null if invalid
 */
const getValidatedMessageContent = (message, content) => {
  const messageContent = message || content;

  if (!messageContent) {
    return null;
  }

  const sanitizedMessage = sanitizeHtmlContent(messageContent);

  if (!sanitizedMessage) {
    return null;
  }

  return sanitizedMessage;
};

/**
 * Validates variant against supported variants list
 * @param {string|undefined} variant - Message variant type
 * @returns {string} Valid variant or default variant
 */
const getValidatedVariant = (variant) => {
  if (variant && SUPPORTED_VARIANTS.includes(variant)) {
    return variant;
  }
  return DEFAULT_CONFIG.variant;
};

/**
 * Validates and extracts message configuration with all supported properties
 * @param {object} messageData - Parsed message data
 * @returns {object|null} Validated message config or null
 */
const getValidatedMessageConfig = (messageData) => {
  if (!messageData) {
    return null;
  }

  const {
    title,
    heading,
    variant,
    message,
    content,
    isActive,
    active,
    hasCloseButton,
  } = messageData;

  // Check if message is active
  const messageActiveStatus = isMessageActiveStatus(isActive, active);
  if (!messageActiveStatus) {
    return null;
  }

  // Validate and sanitize message content
  const sanitizedMessage = getValidatedMessageContent(message, content);
  if (!sanitizedMessage) {
    return null;
  }

  // Support multiple field names for title
  const messageTitle = title || heading;

  // Validate variant
  const messageVariant = getValidatedVariant(variant);

  return {
    title: messageTitle || null,
    variant: messageVariant,
    message: sanitizedMessage,
    isActive: messageActiveStatus,
    hasCloseButton: hasCloseButton !== undefined ? hasCloseButton : DEFAULT_CONFIG.showCloseButton,
  };
};

export default function FasatHomeMessageAlert() {
  const [t] = useTranslation();

  const url = t(staticLabelKeys.FASAT_HOME_MESSAGE);
  const splittedUrl = url.split(' ');

  const check = (link) => (
    <>
      <br />
      <a href={link}>{t(staticLabelKeys.FASAT_HOME_MESSAGE_CLICK)}</a>
      <br />
    </>
  );

  // Get important info message from translation and memoize parsing/validation
  const validatedMessages = useMemo(() => {
    const fasatImportantInfoString = t(staticLabelKeys.FASAT_EXTERNAL_HOME_INFO_MESSAGE);
    // Safely parse and validate the JSON (locale auto-detected or defaults to 'en')
    const parsedImportantInfoArray = parseImportantInfoJson(fasatImportantInfoString);
    // Validate each message in the array
    return parsedImportantInfoArray
      ? parsedImportantInfoArray.map(getValidatedMessageConfig).filter(Boolean)
      : [];
  }, [t]);

  return (
    <div className={`${serverErrorMsgContainer}`}>
      {/* Original static message card */}
      <FasatMessageCard
        hasCloseButton
        titleText={t(staticCommonLabelKeys.COMMON_LABEL_IMPORTANT_INFO)}
        variant="primary_information"
      >
        {splittedUrl.map((_idx, key) => (splittedUrl[key].match(/\bhttps?:\/\/\S+/gi) ? check(splittedUrl[key]) : ` ${splittedUrl[key]}`))}
      </FasatMessageCard>

      {/* Dynamic important info message cards - renders all valid and active messages */}
      {validatedMessages.map((messageConfig, index) => (
        <FasatMessageCard
          key={`important-info-${index}`}
          hasCloseButton={messageConfig.hasCloseButton}
          titleText={
            messageConfig.title
            || t(DEFAULT_CONFIG.title)
          }
          variant={messageConfig.variant}
        >
          <div dangerouslySetInnerHTML={{ __html: messageConfig.message }} />
        </FasatMessageCard>
      ))}
    </div>
  );
}
